/**
 * IosInstallNudge — persistent banner shown to iOS Safari users who
 * haven't yet installed Law OS to their home screen. Push notifications
 * on iOS ONLY work for installed PWAs (Apple restriction), so this
 * banner is critical for delivery reliability.
 *
 * Design decisions (user request):
 *   • Persistent — does NOT allow a "×" dismiss. Only hides once the
 *     user actually installs (detected via display-mode: standalone).
 *   • Includes a step-by-step visual walkthrough triggered by "Show me how".
 *   • Only shows on iOS Safari. Never on Android, desktop, or already-
 *     installed PWA.
 */
import { useEffect, useState } from "react";
import { Smartphone, Share as ShareIcon, PlusSquare, X } from "lucide-react";

function isIosSafari() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return isIos && isSafari;
}

function isInstalled() {
    if (typeof window === "undefined") return true;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator.standalone === true
    );
}

export default function IosInstallNudge() {
    const [visible, setVisible] = useState(false);
    const [showSteps, setShowSteps] = useState(false);

    useEffect(() => {
        // Also poll every 5s so if user completes install with the app open,
        // the banner disappears without a manual refresh.
        const check = () => setVisible(isIosSafari() && !isInstalled());
        check();
        const t = setInterval(check, 5000);
        return () => clearInterval(t);
    }, []);

    if (!visible) return null;

    return (
        <div className="sticky top-0 z-50 bg-amber-500 text-black shadow-md" data-testid="ios-install-nudge">
            <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-2.5">
                <Smartphone className="w-5 h-5 shrink-0"/>
                <div className="text-xs flex-1 min-w-0">
                    <span className="font-medium">Install Law OS for hearing alerts</span>
                    <span className="ml-2 opacity-80 hidden sm:inline">iOS blocks push notifications until you add this app to your home screen.</span>
                </div>
                <button onClick={() => setShowSteps(true)}
                    data-testid="ios-install-nudge-show-steps"
                    className="text-[10px] uppercase tracking-widest bg-black text-amber-500 hover:bg-neutral-900 px-3 py-1.5 whitespace-nowrap">
                    Show me how
                </button>
            </div>
            {showSteps && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80"
                    data-testid="ios-install-nudge-modal">
                    <div className="w-full sm:max-w-md bg-background text-foreground border border-border p-6 sm:mx-4 relative">
                        <button onClick={() => setShowSteps(false)}
                            data-testid="ios-install-nudge-close"
                            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
                            <X className="w-5 h-5"/>
                        </button>
                        <div className="overline mb-1">iOS installation</div>
                        <h3 className="font-serif text-2xl mb-4">Add Law OS to your home screen</h3>
                        <ol className="space-y-4 text-sm">
                            <li className="flex gap-3">
                                <span className="w-6 h-6 border border-primary text-primary rounded-full flex items-center justify-center text-xs shrink-0">1</span>
                                <div>
                                    Tap the <ShareIcon className="w-4 h-4 inline mx-1 text-primary"/><b>Share</b> button
                                    <span className="text-muted-foreground"> (the square with an up-arrow, at the bottom of Safari)</span>
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-6 h-6 border border-primary text-primary rounded-full flex items-center justify-center text-xs shrink-0">2</span>
                                <div>
                                    Scroll and tap <PlusSquare className="w-4 h-4 inline mx-1 text-primary"/><b>Add to Home Screen</b>
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-6 h-6 border border-primary text-primary rounded-full flex items-center justify-center text-xs shrink-0">3</span>
                                <div>
                                    Tap <b>Add</b> in the top-right corner. Law OS will appear on your home screen.
                                </div>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-6 h-6 border border-emerald-500 text-emerald-500 rounded-full flex items-center justify-center text-xs shrink-0">4</span>
                                <div>
                                    Open Law OS from your <b>home screen icon</b> (not Safari). This banner will disappear and push notifications will start working.
                                </div>
                            </li>
                        </ol>
                        <div className="mt-6 text-[11px] text-muted-foreground">
                            You never see notifications while Safari is closed until you complete these four steps. Every leading law-tech app on iOS requires this — it's an Apple platform restriction, not a Law OS choice.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
